import { Component, type ErrorInfo, type ReactNode } from "react";
import { recordDebug } from "@/lib/debugLog";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Barreira de erro: evita tela branca quando algo quebra na renderização,
// mostra uma mensagem amigável e registra o problema no painel de Debug.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    recordDebug(
      "error",
      `Falha ao exibir a tela: ${error.message}`,
      `${error.stack ?? ""}\n${info.componentStack ?? ""}`,
    );
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="w-full max-w-lg bg-background-50 border border-background-200 rounded-lg p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-5">
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl" />
            </div>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">
            Algo saiu do previsto por aqui
          </h1>
          <p className="text-sm text-foreground-600 leading-relaxed mb-6">
            Encontramos um problema ao montar esta tela. Você pode recarregar a página para tentar de novo — os
            detalhes técnicos ficaram registrados no painel de Debug (canto inferior da tela).
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-semibold rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line" />
              </span>
              Recarregar página
            </button>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-background-300/60 text-foreground-800 font-semibold rounded-md hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line" />
              </span>
              Ir para a Home
            </a>
          </div>
          <p className="text-xs text-foreground-400 font-mono mt-6 break-words">{error.message}</p>
        </div>
      </main>
    );
  }
}